<template>
  <v-row>
    <!-- add field -->
    <v-col v-if="!disabled" cols="12">
      <v-overflow-btn
        v-model="overflowBtn"
        :items="overflowBtnItems"
        :label="$t('fresh-vue-forms.FormEditor.action-add-field')"
        segmented
        hide-details
      />
    </v-col>

    <!-- schema -->
    <v-col v-if="schema.fields.length > 0" cols="12">
      <draggable
        v-model="schema.fields"
        tag="v-expansion-panels"
        handle=".handle"
        :component-data="{
          props: {
            hover: true,
            disabled: disabled,
            value: panels
          },
          on: {
            change: ($event) => (panels = $event)
          }
        }"
      >
        <v-expansion-panel v-for="(item, index) in schema.fields" :key="index">
          <ValidationObserver ref="observer" v-slot="{ invalid }">
            <!-- field header -->
            <v-expansion-panel-header class="py-0 pl-0">
              <v-list-item :dense="disabled" :disabled="disabled">
                <!-- move field -->
                <v-list-item-action v-if="!disabled">
                  <!-- invalid identifier -->
                  <v-icon v-if="invalid" color="error">
                    $freshvueforms-error
                  </v-icon>
                  <v-icon v-else class="handle">$freshvueforms-drag</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.label | trim(75) | emptyString }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ getFieldByType(item.type)?.text }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- delete field -->
                <v-list-item-action v-if="!disabled">
                  <v-btn
                    icon
                    @mousedown.stop
                    @mouseup.stop
                    @click.stop.prevent="deleteField({ item, index })"
                  >
                    <v-icon small>$freshvueforms-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-expansion-panel-header>
            <!-- field content -->
            <v-expansion-panel-content eager>
              <!-- label -->
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                :name="$t('fresh-vue-forms.FormEditor.settings.name')"
              >
                <v-text-field
                  v-model="item.label"
                  :label="$t('fresh-vue-forms.FormEditor.settings.name')"
                  :error-messages="errors"
                  outlined
                  @input="onLabelChange({ item, index })"
                />
              </ValidationProvider>

              <!-- value -->
              <ValidationProvider
                v-if="['paragraph'].includes(item.type)"
                v-slot="{ errors }"
                rules="required"
                :name="$t('fresh-vue-forms.FormEditor.settings.text')"
              >
                <editor
                  v-model="item.value"
                  :label="$t('fresh-vue-forms.FormEditor.settings.text')"
                  :error-messages="errors"
                  outlined
                />
              </ValidationProvider>

              <!-- items -->
              <ValidationProvider
                v-if="['select'].includes(item.type)"
                v-slot="{ errors }"
                rules="required|min:1"
                :name="$t('fresh-vue-forms.FormEditor.settings.values')"
              >
                <v-combobox
                  v-model="item.items"
                  :label="$t('fresh-vue-forms.FormEditor.settings.values')"
                  :error-messages="errors"
                  multiple
                  chips
                  deletable-chips
                  outlined
                />
              </ValidationProvider>

              <!-- rules -->
              <template
                v-if="
                  ['text', 'textarea', 'select', 'upload'].includes(item.type)
                "
              >
                <v-subheader>
                  {{
                    $t('fresh-vue-forms.FormEditor.settings.subheaders.rules')
                  }}
                </v-subheader>
                <v-checkbox
                  :label="$t('fresh-vue-forms.FormEditor.settings.required')"
                  :input-value="
                    getValidation({
                      item,
                      index,
                      type: 'required'
                    })
                  "
                  @change="
                    setValidation({
                      item,
                      index,
                      type: 'required',
                      value: $event
                    })
                  "
                />
              </template>

              <!-- options -->
              <template v-if="['select', 'upload'].includes(item.type)">
                <v-subheader>
                  {{
                    $t('fresh-vue-forms.FormEditor.settings.subheaders.options')
                  }}
                </v-subheader>

                <!-- multiple -->
                <v-checkbox
                  v-if="['select', 'upload'].includes(item.type)"
                  v-model="item.multiple"
                  :label="
                    item.type === 'upload'
                      ? $t('fresh-vue-forms.FormEditor.settings.multiple-files')
                      : $t('fresh-vue-forms.FormEditor.settings.multiple-other')
                  "
                />

                <!-- style -->
                <v-select
                  v-if="['select'].includes(item.type)"
                  v-model="item.style"
                  :label="$t('fresh-vue-forms.FormEditor.settings.style')"
                  :items="[
                    {
                      text: $t(
                        'fresh-vue-forms.FormEditor.settings.style-dropdown'
                      ),
                      value: 'dropdown'
                    },
                    {
                      text: $t(
                        'fresh-vue-forms.FormEditor.settings.style-checkbox'
                      ),
                      value: 'checkbox'
                    }
                  ]"
                  outlined
                />
              </template>
            </v-expansion-panel-content>
          </ValidationObserver>
        </v-expansion-panel>
      </draggable>
    </v-col>

    <!-- no data -->
    <v-col v-else cols="12" justify="center">
      {{ $t('fresh-vue-forms.FormEditor.no-data') }}
    </v-col>
  </v-row>
</template>

<script>
import slugify from 'slugify'

import draggable from 'vuedraggable'
import Editor from './Editor'

export default {
  name: 'FormEditor',

  components: {
    draggable,
    Editor
  },

  filters: {
    trim: function (value, length) {
      if (!value) return ''
      let suffix = ''

      if (value.length > length) {
        suffix = '...'
      }

      return value.substring(0, length) + suffix
    }
  },

  props: {
    value: {
      type: Object,
      required: false,
      default: null
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false
    },

    additionalFields: {
      type: Array,
      required: false,
      default: null
    },

    generateModelName: {
      type: Function,
      required: false,
      default: null
    }
  },

  data() {
    return {
      overflowBtn: null,
      schema: null,
      panels: null
    }
  },

  computed: {
    fields() {
      return [
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.subheader'),
          type: 'subheader'
        },
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.paragraph'),
          type: 'paragraph'
        },
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.text'),
          type: 'text'
        },
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.textarea'),
          type: 'textarea'
        },
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.select'),
          type: 'select',
          style: 'dropdown',
          items: []
        },
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.upload'),
          type: 'upload'
        },
        ...(this.additionalFields ?? []),
        {
          text: this.$t('fresh-vue-forms.FormEditor.fields.step'),
          type: 'step'
        }
      ]
    },

    overflowBtnItems() {
      return this.fields.map((field) => ({
        text: field.text,
        disabled: !!field.disabled,
        callback: () => this.addField(field)
      }))
    }
  },

  watch: {
    value: {
      handler: function (value) {
        if (!value || !value.fields) {
          this.generateDefaultSchema()
        } else {
          this.schema = value
        }
      },
      immediate: true
    },

    schema: {
      handler: function (schema) {
        this.$emit('input', schema)
      },
      immediate: true
    },

    disabled: {
      handler(disabled) {
        if (disabled) {
          this.panels = null
        }
      }
    }
  },

  methods: {
    getFieldByType(type) {
      return this.fields?.find((field) => field.type === type) ?? null
    },

    addField(field) {
      // First we make sure, that there is a schema defined
      if (!this.schema || !this.schema.fields) {
        this.generateDefaultSchema()
      }

      // If the field is disabled, we can't add it
      if (field?.disabled) {
        return false
      }

      // add a copy of the data to the schema
      this.schema.fields.push({ ...field })
    },

    deleteField({ index }) {
      this.schema.fields.splice(index, 1)
    },

    generateDefaultSchema() {
      this.schema = {
        fields: []
      }
    },

    onLabelChange({ item }) {
      if (!['subheader', 'paragraph'].includes(item.type)) {
        item.model = this.generateModelName?.(item) ?? slugify(item.label, '_')
      }
    },

    getRulesAsArray(rules) {
      return rules ? rules.split('|') : []
    },

    getValidation({ item, type }) {
      const rules = this.getRulesAsArray(item.rules)

      return rules.includes(type)
    },

    setValidation({ item, type, value }) {
      const rules = this.getRulesAsArray(item.rules)

      if (type === 'required') {
        if (value) {
          // required true
          // add required if not already there
          if (!rules.includes(type)) {
            rules.push('required')
          }
        } else {
          // required false
          // remove required if it exists
          if (rules.includes(type)) {
            rules.splice(rules.indexOf(type), 1)
          }
        }
      }

      item.rules = rules.length > 0 ? rules.join('|') : null
    }
  }
}
</script>

<style scoped>
.handle {
  cursor: grab;
}
</style>
